import React, { useCallback, useEffect, useState, useContext } from 'react';
import { Col, Container, Row, Button } from 'react-bootstrap';

import AppContext from '../../contexts/app-context';
import { ApiServiceServerless } from '../../xhr_libs';
import PageHeader from '../../components/PageHeader';

import Loader from '../../components/Loader';
import {
  BuildingTypes,
  BUILDING_BENCHMARK_OPTIONS,
} from '../../helpers/BuildingTypes';

import Helmet from 'react-helmet';
import { useOrganizationRoles } from '../../helpers/checkRoles';
import EditBuildingModal from '../../components/modals/BuildingModals/EditBuildingModal';
import ConfirmationModal, {
  useConfirm,
} from '../../components/modals/ConfirmationModal';
import BuildingAccountsTable from './BuildingAccountsTable';
import ResourceDetails from '../../components/ResourceDetails';
import AddUtilityAccountModal from '../../components/modals/UtilityAccountModals/AddUtilityAccountModal';

const pageTitle = 'Building: ';

const styles = { button: { marginRight: '0.5em', marginLeft: '0.5em' } };

const DEFAULT_BUILDING_VALUES = {
  name: '',
  square_footage: '',
  address: {},
  not_physical: '',
  organization_id: null,
  reference_id: '',
  organization: { name: '' },
  utility_acconts: [],
  has_provider_account: false,
  building_type: null,
  benchmark_type: null,
  accounts: [],
  siblings: [],
};

export default function EditBuilding(props) {
  const { showToast, match, history } = props;
  const { userSelectedOrganizationDetails, setActiveOrganization } =
    useContext(AppContext);
  const [confirmationModalProps, withConfirm] = useConfirm();

  const [building, setBuilding] = useState({ ...DEFAULT_BUILDING_VALUES });

  const [isLoading, setIsLoading] = useState(true);
  const [showEditModal, setShowEditModal] = useState(false);

  const [showUtilityModal, setShowUtilityModal] = useState(false);

  const updateAccess = useOrganizationRoles(
    userSelectedOrganizationDetails,
    'building',
    'update'
  );

  const deleteAccess = useOrganizationRoles(
    userSelectedOrganizationDetails,
    'building',
    'delete'
  );

  const accountCreateAccess = useOrganizationRoles(
    userSelectedOrganizationDetails,
    'utility_account',
    'create'
  );

  const accountUpdateAccess = useOrganizationRoles(
    userSelectedOrganizationDetails,
    'utility_account',
    'update'
  );

  useEffect(() => {
    if (
      building.organization_id &&
      building.organization_id !== userSelectedOrganizationDetails.id
    ) {
      setActiveOrganization({ id: building.organization_id, name: '' });
    }
  }, [building, userSelectedOrganizationDetails, setActiveOrganization]);

  const disableProviderAccounts = (building) => {
    ApiServiceServerless.post(
      `/providers/disable/building/${match.params.id}`,
      {},
      {
        authorization_id: match.params.id,
        alternate_authorization_id: building.organization_id,
      }
    )
      .then(() => {
        deleteBuilding();
      })
      .catch((error) => {
        showToast('danger', 'Error', error);
        console.error(error);
        return false;
      });
  };

  const deleteBuilding = () => {
    ApiServiceServerless.delete(`buildings/${match.params.id}`, {
      authorization_id: match.params.id,
    })
      .then(() => {
        showToast(
          'success',
          'Success',
          `Building "${building.name}" was deleted.`
        );
        history.push('/organizations');
      })
      .catch((error) => {
        showToast('danger', 'Error', error);
        throw error;
      });
  };

  const onDeleteClick = () => {
    let msg = `Are you sure you want to delete ${building.name}?  This cannot be undone.`;

    const has_provider_account = building['has_provider_account'];
    if (has_provider_account) {
      msg +=
        ' Additionally, any Automated Billing accounts assoicatied with this building will be disabled';
    }

    const beginBuildingDelete = () => {
      if (has_provider_account) {
        disableProviderAccounts(building);
      } else {
        deleteBuilding();
      }
    };
    withConfirm(msg, beginBuildingDelete);
  };

  const getBuilding = useCallback(() => {
    ApiServiceServerless.get(`buildings/${match.params.id}`, {
      authorization_id: match.params.id,
    })
      .then((res) => {
        setBuilding({
          ...res.data,
        });
      })
      .catch((error) => {
        showToast('danger', 'Error', error);
        throw error;
      });
  }, [showToast, match.params.id]);

  useEffect(() => {
    setIsLoading(true);
    getBuilding();
    setIsLoading(false);
  }, [getBuilding]);

  const handleSetNewAccount = (data) => {
    setBuilding((prev) => ({ ...prev, accounts: [...prev.accounts, data] }));
  };

  const handleSetBuilding = (data) => {
    setBuilding((prev) => ({
      ...data,
      organization: prev.organization,
      accounts: prev.accounts,
      siblings: prev.siblings,
    }));
  };

  return isLoading ? (
    <Loader />
  ) : (
    <>
      <ConfirmationModal {...confirmationModalProps} />
      <Container className='et-container'>
        <Helmet>
          <title>
            {pageTitle}
            {building.name}
          </title>
        </Helmet>
        <PageHeader
          titlePrepend={pageTitle}
          title={building.name ? building.name : 'Manage Building'}
        />

        <Row style={{ marginBottom: '2em' }}>
          <Col md={2}>
            <ResourceDetails
              header={'Address'}
              value={building.address}
              variant={'address'}
            />
          </Col>
          <Col md={2}>
            <ResourceDetails
              header={'Organization'}
              value={building.organization.name}
              variant={'link'}
              link_route={`/organizations/${building.organization.id}`}
            />
          </Col>

          <Col md={2}>
            <ResourceDetails
              header={'Reference ID'}
              value={building.reference_id}
            />
          </Col>

          <Col md={2}>
            <ResourceDetails
              header={'Building Type'}
              value={
                building.building_type
                  ? BuildingTypes[building.building_type]
                  : null
              }
            />
          </Col>

          <Col md={2}>
            <ResourceDetails
              header={'Utility Usage Profile'}
              value={
                building.benchmark_type
                  ? BUILDING_BENCHMARK_OPTIONS[building.benchmark_type]
                  : null
              }
            />
          </Col>
          <Col md={2}>
            <ResourceDetails
              header={'Area'}
              value={building.not_physical ? null : building.square_footage}
              variant={'number'}
              suffix={'ft²'}
            />
          </Col>
        </Row>
        <div style={{ width: '100%', textAlign: 'right', marginBottom: '1em' }}>
          {updateAccess && (
            <Button
              variant='edit'
              style={styles.button}
              onClick={() => setShowEditModal(true)}
            >
              Edit Building
            </Button>
          )}

          {deleteAccess && (
            <Button
              variant='danger'
              style={styles.button}
              onClick={() => onDeleteClick(true)}
            >
              Delete Building
            </Button>
          )}
          {accountCreateAccess && (
            <Button
              variant='success'
              style={styles.button}
              onClick={() => setShowUtilityModal(true)}
            >
              Add Utility Account
            </Button>
          )}
        </div>

        <BuildingAccountsTable
          building={building}
          updateAccess={accountUpdateAccess}
          showToast={showToast}
          setBuilding={setBuilding}
        />

        <div style={{ marginBottom: '20px' }} />
        <EditBuildingModal
          show={showEditModal}
          onHide={() => setShowEditModal(false)}
          building_id={match.params.id}
          building={building}
          organization_name={building.organization.name}
          handleSetBuilding={handleSetBuilding}
          isLoading={false}
          showToast={showToast}
        />
        <AddUtilityAccountModal
          organization={building.organization}
          buildings={building.siblings}
          defaultBuildings={[building.id]}
          setDefaultBuildings={() => {}}
          show={showUtilityModal}
          onHide={() => {
            setShowUtilityModal(false);
          }}
          openAccountModal={() => setShowUtilityModal(true)}
          handleSetAccount={handleSetNewAccount}
          showToast={showToast}
          history={history}
        />
      </Container>
    </>
  );
}
